import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Set up worker - more reliable method
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const SecurePdfViewer = ({ pdfUrl, compilation, onClose }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.2);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  
  // Check if PDF URL is valid
  useEffect(() => {
    if (!pdfUrl) {
      setError("No PDF URL provided");
      setLoading(false);
    }
    // Log the URL to check if it's valid
    console.log("PDF URL:", pdfUrl);
  }, [pdfUrl]);

  // Secure view measures - applied to the component's container, not the entire document
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Prevent right-click context menu
    const preventContextMenu = (e) => {
      e.preventDefault();
      return false;
    };

    // Prevent keyboard shortcuts for screenshot/download/print
    const preventKeyboard = (e) => {
      if (
        (e.ctrlKey && (e.key === 's' || e.key === 'p' || e.key === 'S' || e.key === 'P')) ||
        e.key === 'PrintScreen' ||
        (e.ctrlKey && e.shiftKey && e.key === 'S') ||
        (e.altKey && e.key === 'Print') ||
        (e.metaKey && (e.key === 's' || e.key === 'p' || e.key === 'S' || e.key === 'P'))
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    // Prevent copy/paste
    const preventCopy = (e) => {
      e.preventDefault();
      return false;
    };
    
    // Add all event listeners to the container, not the document
    container.addEventListener('contextmenu', preventContextMenu);
    window.addEventListener('keydown', preventKeyboard, true);
    window.addEventListener('keyup', preventKeyboard, true);
    container.addEventListener('copy', preventCopy);
    container.addEventListener('cut', preventCopy);
    
    return () => {
      container.removeEventListener('contextmenu', preventContextMenu);
      window.removeEventListener('keydown', preventKeyboard, true);
      window.removeEventListener('keyup', preventKeyboard, true);
      container.removeEventListener('copy', preventCopy);
      container.removeEventListener('cut', preventCopy);
    };
  }, [containerRef.current]);

  // Simplified dev tools detection - less likely to cause issues
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // Skip in development mode to allow debugging
      return;
    }
    
    const blockDevTools = () => {
      // Simple dev tools detection
      const checkDevTools = () => {
        if (window.outerWidth - window.innerWidth > 160 || 
            window.outerHeight - window.innerHeight > 160) {
          window.blur();
          alert('Developer tools are not allowed while viewing this content.');
          onClose();
        }
      };
      
      const interval = setInterval(checkDevTools, 1000);
      return () => clearInterval(interval);
    };
    
    return blockDevTools();
  }, [onClose]);

  // Add watermark overlay
  useEffect(() => {
    if (!overlayRef.current || !compilation?.title) return;
    
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      ctx.font = '24px Arial';
      ctx.fillStyle = 'rgba(128, 128, 128, 0.1)';
      ctx.textAlign = 'center';
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(-Math.PI / 4);
      
      const watermarkText = `${compilation.title} - ${new Date().toLocaleDateString()}`;
      for (let i = -5; i < 5; i++) {
        for (let j = -5; j < 5; j++) {
          ctx.fillText(watermarkText, i * 200, j * 200);
        }
      }

      overlayRef.current.style.backgroundImage = `url(${canvas.toDataURL()})`;
    } catch (error) {
      console.error("Error creating watermark:", error);
    }
  }, [compilation?.title]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log("PDF loaded successfully with", numPages, "pages");
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setError('Failed to load PDF. Please try again. Error: ' + error.message);
    setLoading(false);
  };

  const changePage = (direction) => {
    setPageNumber(prev => {
      const newPage = prev + direction;
      return Math.max(1, Math.min(newPage, numPages || 1));
    });
  };

  const zoomIn = () => setScale(prev => Math.min(prev + 0.1, 2.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.1, 0.5));

  return (
    <div className="fixed inset-0 bg-black z-50" ref={containerRef}>
      {/* Navigation Bar */}
      <div className="absolute top-0 left-0 right-0 bg-gray-900 text-white p-4 flex justify-between items-center z-10">
        <div className="flex items-center space-x-4">
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h2 className="text-lg font-semibold truncate max-w-md">{compilation?.title || "PDF Viewer"}</h2>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={() => changePage(-1)}
            disabled={pageNumber <= 1}
            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <span className="text-sm">
            Page {pageNumber} of {numPages || '--'}
          </span>

          <button
            onClick={() => changePage(1)}
            disabled={pageNumber >= numPages}
            className="p-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-800 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <div className="border-l border-gray-700 h-6 mx-2"></div>

          <button
            onClick={zoomOut}
            className="p-2 hover:bg-gray-800 rounded"
            title="Zoom Out"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </button>

          <span className="text-sm w-12 text-center">{Math.round(scale * 100)}%</span>

          <button
            onClick={zoomIn}
            className="p-2 hover:bg-gray-800 rounded"
            title="Zoom In"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
          </button>
        </div>
      </div>

      {/* PDF Viewer Container */}
      <div className="pt-16 h-full overflow-auto bg-gray-800 relative">
        {/* Watermark Overlay */}
        <div 
          ref={overlayRef}
          className="absolute inset-0 pointer-events-none z-20"
          style={{ mixBlendMode: 'overlay' }}
        />

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-white text-lg flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
              Loading PDF...
              <div className="text-sm text-gray-400 mt-2">URL: {pdfUrl ? pdfUrl.substring(0, 50) + (pdfUrl.length > 50 ? '...' : '') : 'No URL provided'}</div>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-red-400 text-lg max-w-md text-center p-4">
              <div className="mb-4">⚠️ {error}</div>
              <div className="text-sm">
                Please check if the PDF URL is correct and accessible:
                <div className="bg-gray-700 p-2 rounded mt-2 break-all text-gray-300">
                  {pdfUrl || "No URL provided"}
                </div>
              </div>
              <button 
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-600"
              >
                Close Viewer
              </button>
            </div>
          </div>
        )}

        {/* PDF Document */}
        {!error && (
          <div className="flex justify-center py-8">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={<div className="text-white">Loading document...</div>}
              error={<div className="text-red-400">Error loading PDF!</div>}
              options={{
                cMapUrl: 'https://unpkg.com/pdfjs-dist@3.4.120/cmaps/',
                cMapPacked: true,
              }}
            >
              {!loading && !error && (
                <Page 
                  key={`page_${pageNumber}`}
                  pageNumber={pageNumber} 
                  scale={scale}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  error={<div className="text-red-400">Error rendering page {pageNumber}!</div>}
                />
              )}
            </Document>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurePdfViewer;